import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { convertYearMonthyWeek } from "../utils/date";
import { EDiary, EIsBackTest, EPeriod } from "../interfaces/Filters";

export interface IParamsPositionPerformance {
  productsId?: number[];
  symbols?: string[];
  is_backtest?: EIsBackTest;
  diary?: EDiary;
  period?: EPeriod;
  close_date_init?: Date;
  close_date_end?: Date;
}

export interface IResponsePositionPerformance {
  data: {
    date: string;
    value: number;
    value_text: string;
    value_acumulated: number;
    value_acumulated_text: string;
    percentage: number;
    percentage_text: string;
    percentage_acumulated: number;
    percentage_acumulated_text: string;
  }[];
  backtest_date: string;
  available_symbols: string[];
  assertivity: string;
  avg_daily: string;
  max_gain: string;
  max_loss: string;
  profitability: string;
  qty_days: string;
  qty_gain_days: string;
  qty_loss_days: string;
  total: string;
  total_gain: string;
  total_loss: string;
  total_acumulated: string;
}

const getPositionsPerformance = async (
  {
    productsId,
    symbols,
    is_backtest,
    close_date_init,
    close_date_end,
  }: IParamsPositionPerformance,
  signal?: AbortSignal
) => {
  const params = new URLSearchParams();
  if (!!productsId && productsId.length > 0) {
    productsId.forEach((productId) =>
      !!productId ? params.append("product_id", productId.toString()) : null
    );
  }
  if (!!symbols && symbols.length > 0) {
    symbols.forEach((symbol) =>
      !!symbol ? params.append("symbol", symbol.toString()) : null
    );
  }

  const convertCloseDateInit = convertYearMonthyWeek(close_date_init);
  const convertCloseDateEnd = convertYearMonthyWeek(close_date_end);
  if (!!is_backtest) params.append("is_backtest", is_backtest);
  if (!!convertCloseDateInit)
    params.append("close_date_init", convertCloseDateInit);
  if (!!convertCloseDateEnd)
    params.append("close_date_end", convertCloseDateEnd);

  const { data } = await api.get<IResponsePositionPerformance>(
    `web/positions/performance`,
    {
      params,
      signal,
    }
  );

  return data;
};

export const usePositionsPerformance = (params: IParamsPositionPerformance) => {
  // Normaliza a queryKey para evitar re-renders desnecessários
  // Converte objetos Date para timestamps e arrays para strings ordenadas
  const normalizedKey = [
    "PositionsPerformance",
    params.productsId?.slice().sort().join(",") || "",
    params.symbols?.slice().sort().join(",") || "",
    params.is_backtest || "",
    params.diary || "",
    params.period || "",
    params.close_date_init?.getTime() || "",
    params.close_date_end?.getTime() || "",
  ];

  return useQuery({
    queryKey: normalizedKey,
    queryFn: () => getPositionsPerformance(params),
    retry: 0,
  });
};
